import { useEffect } from 'react';
import { overlayModule, useOverlayModule } from '../../app/lazyOverlay';
import type { ReviewDialogProps } from './ReviewPage';

// The lazy boundary in front of the review-and-publish dialog (T-321 / BUG-155; REQ-280 AC1, ARCH §8.1).
//
// `app/staged/StagedTray.tsx` is mounted by the shell on every framed route, and it imported `ReviewDialog`
// statically — so the whole review screen (the ticked list, the impact preview, the guardrail band, the
// publish result, the conflict confirm, its copy table: 20.4 KB of the eager chunk's generated bytes) was
// downloaded and compiled before the first paint by every reader, including one who never stages anything.
// It is behind two presses (the staged pill, then "Review n changes") and renders nothing until the second,
// so it is as clean a split as the search palette's.
//
// The dialog is raised from a press, so the chunk is PREFETCHED ON IDLE — but only where the tray exists at
// all (`useReviewDialogPrefetch` is called by the tray's own provider), and the idle callback means it never
// competes with the first number (BUG-155's own lesson, see `app/period/PeriodProvider.tsx`). By the time
// anyone has opened the tray and read it the module is in memory and the dialog is drawn in the same commit
// as the press.
//
// T-323 / BUG-157: the boundary is `app/lazyOverlay.ts`, not `React.lazy` + `Suspense` — a boundary that
// suspends on the press that mounts it livelocks React's synchronous `act()` flush and eats a 4 GB heap
// (`routes/home/HomeBriefing.test.tsx` died here; the whole story is in that file's comment). Nothing on
// screen changes: before, the `Suspense` fallback was `null`, because the only thing on screen before the
// dialog opens is the tray panel it replaces.

const dialog = overlayModule(() => import('./ReviewPage'));

/** Warm the chunk. Safe to call repeatedly: the load is shared. */
export function prefetchReviewDialog(): void {
  void dialog.load();
}

/** Ask for the chunk once the browser is idle — the `search/lazy.tsx` pattern, same cancellation. */
export function useReviewDialogPrefetch(): void {
  useEffect(() => {
    const idle = (globalThis as { requestIdleCallback?: typeof requestIdleCallback })
      .requestIdleCallback;
    if (typeof idle === 'function') {
      const handle = idle(() => prefetchReviewDialog(), { timeout: 4000 });
      const cancel = (globalThis as { cancelIdleCallback?: typeof cancelIdleCallback })
        .cancelIdleCallback;
      return () => cancel?.(handle);
    }
    const timer = setTimeout(prefetchReviewDialog, 2500);
    return () => clearTimeout(timer);
  }, []);
}

/**
 * The dialog, code-split. Renders nothing at all until it is opened, exactly as `ReviewDialog` does for a
 * closed `open` — so the tray's "the review dialog outlives the panel" arrangement is unchanged.
 */
export function LazyReviewDialog(props: ReviewDialogProps) {
  const module = useOverlayModule(dialog, props.open);
  if (!props.open || module === null) return null;
  const ReviewDialog = module.ReviewDialog;
  return <ReviewDialog {...props} />;
}
